/**
 * ProductRegister (C-1a) — RA-PLM Master Product Register.
 * RA dashboard KPI cards + category/status filters + search + table + add dialog.
 * Route: /regulatory/products
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import {
  useProductsQuery, useCreateProduct, CATEGORY_LABEL, REGSTATUS_LABEL, type NewProductInput,
} from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function catClass(c: string) { return c === 'PHARMA' ? styles.catPharma : c === 'NUTRA' ? styles.catNutra : styles.catMd; }

export function ProductRegister() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const query = useProductsQuery({ status: status || undefined, category: category || undefined, q: q.trim() || undefined });
  const create = useCreateProduct();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewProductInput>({ itemDescription: '', category: 'PHARMA', registrationStatus: 'UNDER_REGISTRATION' });
  const [err, setErr] = useState<string | null>(null);

  const items = query.data?.items ?? [];
  const stats = query.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.itemDescription.trim()) { setErr('Item description is required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ itemDescription: '', category: 'PHARMA', registrationStatus: 'UNDER_REGISTRATION' });
      navigate({ to: '/regulatory/products/$id', params: { id: created.productId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not add the product.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Master Product Register"
        subtitle="Single source of truth for registered & under-registration products — registrations, renewals, and regulatory lifecycle."
        actions={
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>
            Add product
          </button>
        }
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.registered}</span><span className={styles.kpiLabel}>Registered</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.under_registration}</span><span className={styles.kpiLabel}>Under registration</span></div>
          <div className={`${styles.kpi} ${stats.renewals_due_12m > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{stats.renewals_due_12m}</span><span className={styles.kpiLabel}>Renewals due (12mo)</span></div>
          <div className={`${styles.kpi} ${stats.expired > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.expired}</span><span className={styles.kpiLabel}>Expired</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.pharma}/{stats.nutra}/{stats.md}</span><span className={styles.kpiLabel}>Pharma / Nutra / MD</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand / molecule / reg #…" className={styles.search} />
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option><option value="REGISTERED">Registered</option><option value="UNDER_REGISTRATION">Under registration</option>
        </select>
        <select aria-label="Category filter" value={category} onChange={(e) => setCategory(e.target.value)} className={styles.select}>
          <option value="">All categories</option><option value="PHARMA">Pharmaceutical</option><option value="NUTRA">Nutraceutical</option><option value="MD">Medical Device</option>
        </select>
        <span className={styles.resultCount}>{items.length} product{items.length === 1 ? '' : 's'}</span>
      </div>

      {query.isLoading ? (
        <div className={styles.empty}>Loading register…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No products match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr><th>Code</th><th>Product</th><th>Category</th><th>Reg #</th><th>Status</th><th>Renewal due</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.productId} className={styles.row} onClick={() => navigate({ to: '/regulatory/products/$id', params: { id: p.productId } })}>
                <td className={styles.mono}>{p.productCode}</td>
                <td>{p.brandName || p.itemDescription}{p.strength ? <span className={styles.sub}> · {p.strength}{p.strengthUnit ?? ''}</span> : ''}</td>
                <td><span className={`${styles.pill} ${catClass(p.category)}`}>{CATEGORY_LABEL[p.category] ?? p.category}</span></td>
                <td className={styles.mono}>{p.registrationNumber ?? '—'}</td>
                <td><span className={styles.statusPill}>{REGSTATUS_LABEL[p.registrationStatus] ?? p.registrationStatus}</span></td>
                <td>{p.registrationStatus === 'REGISTERED' ? fmtDate(p.renewalDueDate) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="Add product">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Add a product</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Item description *</span>
              <input type="text" value={form.itemDescription} onChange={(e) => setForm({ ...form, itemDescription: e.target.value })} placeholder="e.g. Atorvastatin 20 mg Tablets" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Brand name</span>
                <input type="text" value={form.brandName ?? ''} onChange={(e) => setForm({ ...form, brandName: e.target.value })} /></label>
              <label className={styles.field}><span>Molecule</span>
                <input type="text" value={form.molecule ?? ''} onChange={(e) => setForm({ ...form, molecule: e.target.value })} /></label>
            </div>
            <div className={styles.field2}>
              <label className={styles.field}><span>Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="PHARMA">Pharmaceutical</option><option value="NUTRA">Nutraceutical</option><option value="MD">Medical Device</option>
                </select></label>
              <label className={styles.field}><span>Status</span>
                <select value={form.registrationStatus} onChange={(e) => setForm({ ...form, registrationStatus: e.target.value })}>
                  <option value="UNDER_REGISTRATION">Under registration</option><option value="REGISTERED">Registered</option>
                </select></label>
            </div>
            <div className={styles.field2}>
              <label className={styles.field}><span>Registration #</span>
                <input type="text" value={form.registrationNumber ?? ''} onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })} /></label>
              <label className={styles.field}><span>Certificate issue date</span>
                <input type="date" value={form.certificateIssueDate ?? ''} onChange={(e) => setForm({ ...form, certificateIssueDate: e.target.value })} /></label>
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Adding…' : 'Add product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
